/**
 * A cut or burn at work, answered in the moment (leaves B6.1, B6.3, B6.5; audit AUDIT-be59398-0710 gap 1).
 *
 * The moment: a cook or barista who just cut or burned a hand, behind the bar or at the line, mid-rush, alone or short-staffed,
 * maybe bleeding. The person comes first: the first screen of the existing report ("Do this first", easy.ts "cut-burn") is care
 * for the hand. Then three plain questions, one per screen, each with a picture-sized answer and no wall:
 *   1. Do you need a doctor?      (yes shows the hospital-level steps, with Utah's 8-hour UOSH report, see below)
 *   2. Did blood or a bandage get near food?   (yes or not sure shows what to throw away)
 *   3. Is the cut covered with a bandage and a glove?   (not yet teaches the fix)
 * The answers ride on the report (Incident.injury) and become one row of the Injury log (who = initials, when = date and
 * time), so the binder, Prove, the Food Code index and Form 3-A speak to it. A "not yet" or "not sure" is saved as said and
 * the row says what was ASKED, never that it was done ("Asked to: ... Not confirmed yet."). Nothing here blames, and nothing
 * stops the report.
 *
 * What the sources say (docs/source, quoted so the words can be checked; test/injury.test.ts reads these files):
 *  - parish protocol "Cuts and burns during food work": "Stop work immediately and step away from the food." "Cut: wash the
 *    wound with soap and water, apply pressure to stop bleeding, then bandage. Burn: cool under cool running water for 10-20
 *    minutes; no ice, butter or ointment on the food line". "Throw away any food that blood touched or might have touched."
 *    "Deep cuts, cuts that won't stop bleeding, and burns that blister larger than a quarter, ... get medical care. Call 911
 *    for heavy bleeding or large burns." "Note the injury and the food discarded on the event log."
 *  - Food Code 2-401.13 (parish protocol table "Diseased hands"): a cut, burn or boil on the hands or wrists must be covered
 *    with "an impermeable cover (bandage or finger cot) and a single-use glove"; "A bandage alone, without a glove, is never
 *    enough on the hands." The coffee-shop protocol says the same, and "Discard any drink, milk or pastry that blood touched
 *    or might have touched".
 *  - Food Code 2-401.13 is the section the sources give for covering a wound. The brief also named 2-201.12, 2-401.11,
 *    2-402.11 and 3-305.11: in the sources those are exclusion and restriction, eating and drinking, hair restraints and
 *    where food is stored. None sets what to do with a cut, so none is cited here.
 *  - The parish protocol's first-aid kit line: "Keep a stocked first-aid kit with blue bandages, finger cots, gauze, burn
 *    dressings and gloves ... (OSHA expects one where parish staff work)". OSHA's phone line is in the same protocol:
 *    "www.osha.gov, (800) 321-OSHA (6742)".
 *  - Reporting a severe injury: research/osha-housekeeping.md OS4 (Utah uses R614-1-5 in place of 29 CFR 1904.39, so the federal
 *    24-hour/8-hour numbers are NOT shown) and OS48 (MEASURED, Utah Admin. Code R614-1-5(B), read 2026-09-30): "Each employer shall
 *    within 8 hours of occurrence, notify UOSH of any work-related fatalities, of any disabling, serious, or significant injury and
 *    of any occupational disease incident." UOSH phone (801) 530-6901. The federal OSHA line is not shown: it is the wrong agency here.
 * OUR ADVICE (said so on screen where it applies): asking the three questions in this order and treating "not sure" as "throw it
 * away" (the source says "might have touched").
 *
 * Gates (test/injury.test.ts, each with a control):
 * | J1 Person first | the first step is for the hand, before any food step; the report's first-aid list is the easy-steps list |
 * | J2 Blood near food | "yes" and "not sure" both name the discard (the source says "might have touched"); "no" names none |
 * | J3 A "not yet" teaches | uncovered names bandage plus glove and staying off food work if it can't be covered; no line blames |
 * | J4 Hospital steps | "yes" names 911 for heavy bleeding, telling the person in charge, and the Utah 8-hour UOSH report (R614-1-5(B)); never the federal line or numbers |
 * | J5 Who and when | a row always has date, time and initials; a row without who is refused |
 * | J6 Truthful | the row says "Asked to", never "Done"; an unanswered question is "not answered", never a guess |
 * | J7 Evidence | fc-bare-hand, Form 3-A item 9, the Food Code index (2-401.13) and Prove list the log |
 */
import { packFor } from "./states/index.ts";
import type { UsState } from "./states/types.ts";
import type { Severity } from "./model.ts";

export const INJURY_TEMPLATE = "injury_response";
export const INJURY_CITATION = "Food Code 2-401.13";
export const INJURY_SOURCE = "Source protocols: cuts and burns";

export type InjuryHospital = "yes" | "no";
export type InjuryFood = "no" | "yes" | "not sure";
export type InjuryCovered = "yes" | "not yet";
/** What a person said on the report; a missing key is a question left alone. */
export interface InjuryAnswers { hospital?: InjuryHospital; food?: InjuryFood; covered?: InjuryCovered }

export const INJURY_HOSPITAL_CHOICES: readonly InjuryHospital[] = ["yes", "no"];
export const INJURY_FOOD_CHOICES: readonly InjuryFood[] = ["no", "yes", "not sure"];
export const INJURY_COVERED_CHOICES: readonly InjuryCovered[] = ["yes", "not yet"];

/** The three questions, one per report screen. Titles are the screen titles. */
export const INJURY_QUESTIONS = {
  hospital: { title: "Do you need a doctor?", label: "Do you need a doctor?", choices: [{ value: "yes", label: "Yes, I need a doctor" }, { value: "no", label: "No, I can treat it here" }] },
  food: { title: "Did blood or a bandage get near food?", label: "Did blood or a bandage get near food?", choices: [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }, { value: "not sure", label: "Not sure" }] },
  covered: { title: "Is the cut covered with a bandage and a glove?", label: "Is it covered with a bandage and a glove?", choices: [{ value: "yes", label: "Yes, it is covered" }, { value: "not yet", label: "Not yet" }] },
} as const;

/** What a question's answer shows under the chips: a short line, the steps in order, and the rule behind them. */
export interface InjuryGuide {
  /** "warn" needs an action from the person; "go" needs none. Never a stop sign: the report always goes. */
  tone: "go" | "warn";
  line: string;
  steps: string[];
  citation: string;
  /** True when the line is our own advice and no source in docs/source sets it. */
  ourAdvice?: boolean;
}

/**
 * The state's workplace-safety agency and its reporting rule, from the state pack (states/*.ts `workplace`): Utah runs its
 * own plan (UOSH, R614-1-5(B): research/osha-housekeeping.md OS48, lastChecked 2026-09-30). Never the federal line.
 */
export const oshaPhones = (state?: UsState): string => {
  const phones = packFor(state).workplace.agency.phones;
  // One office: the number. Several (Nevada: south and north): each with the area it serves.
  return phones.length === 1 ? phones[0]!.number : phones.map((p) => `${p.number} (${p.label})`).join(" or ");
};
export const oshaCitation = (state?: UsState): string => packFor(state).workplace.citation;
export const oshaAdviceLine = (state?: UsState): string => `${packFor(state).workplace.reportLine} Call ${oshaPhones(state)}.`;
/** Utah's (the default state), kept for callers that predate the state packs. */
export const OSHA_PHONE = oshaPhones("UT");
export const OSHA_CITATION = oshaCitation("UT");
export const OSHA_ADVICE_LINE = oshaAdviceLine("UT");

export function hospitalGuide(a: InjuryHospital | undefined, state?: UsState): InjuryGuide | undefined {
  if (a === "yes") return {
    tone: "warn",
    line: "Get medical care now.",
    steps: [
      "Call 911 for heavy bleeding or a large burn.",
      "Tell the person in charge now.",
      "Put a clean cloth on the cut and press.",
      oshaAdviceLine(state),
    ],
    citation: `${INJURY_SOURCE} (call 911 for heavy bleeding or large burns; get medical care); ${oshaCitation(state)}`,
  };
  if (a === "no") return {
    tone: "go",
    line: "Deep cuts, bleeding that will not stop, and burns bigger than a quarter need a doctor. Change your answer if that is you.",
    steps: [],
    citation: INJURY_SOURCE,
  };
  return undefined;
}

export function foodGuide(a: InjuryFood | undefined): InjuryGuide | undefined {
  if (a === "yes" || a === "not sure") return {
    tone: "warn",
    line: a === "yes" ? "Throw away that food." : "If it might have touched food, throw that food away.",
    steps: [
      "Throw away any food, drink or pastry that blood touched or might have touched.",
      "Wash, rinse and sanitize the knife, board or tool.",
      "Clean and sanitize the surface.",
      "Tell the person in charge what you threw away.",
    ],
    citation: `${INJURY_SOURCE} (throw away any food that blood touched or might have touched)`,
  };
  if (a === "no") return { tone: "go", line: "Good. Keep it that way: stay off food until it is covered.", steps: [], citation: INJURY_SOURCE };
  return undefined;
}

export function coveredGuide(a: InjuryCovered | undefined): InjuryGuide | undefined {
  if (a === "not yet") return {
    tone: "warn",
    line: "Cover it before you touch food again.",
    steps: [
      "Put on a waterproof bandage or a finger cot. A blue one is easy to spot.",
      "Put a single-use glove over it.",
      "If it will not stay covered, do jobs away from food until it does.",
    ],
    citation: `${INJURY_CITATION}; ${INJURY_SOURCE} (a bandage alone, without a glove, is never enough on the hands)`,
  };
  if (a === "yes") return { tone: "go", line: "Good. Change the bandage if it gets wet or loose.", steps: [], citation: INJURY_CITATION };
  return undefined;
}

export const injuryGuide = (q: keyof typeof INJURY_QUESTIONS, a: string | undefined): InjuryGuide | undefined =>
  q === "hospital" ? hospitalGuide(a as InjuryHospital) : q === "food" ? foodGuide(a as InjuryFood) : coveredGuide(a as InjuryCovered);

/** Parse the answers a client sent; anything else is an error in plain words. */
export function parseInjury(v: unknown): { injury?: InjuryAnswers } | { error: string } {
  if (v === undefined || v === null) return {};
  if (typeof v !== "object" || Array.isArray(v)) return { error: '"injury" must be an object.' };
  const o = v as Record<string, unknown>, out: InjuryAnswers = {};
  for (const [k, list] of [["hospital", INJURY_HOSPITAL_CHOICES], ["food", INJURY_FOOD_CHOICES], ["covered", INJURY_COVERED_CHOICES]] as const) {
    if (o[k] === undefined || o[k] === null || o[k] === "") continue;
    if (typeof o[k] !== "string" || !list.includes(o[k] as never)) return { error: `"injury.${k}" must be one of: ${list.join(", ")}.` };
    (out as Record<string, string>)[k] = o[k] as string;
  }
  return Object.keys(out).length ? { injury: out } : {};
}

/** The lines the manager sees under the report: "Doctor needed: yes", "Blood near food: not sure", "Covered: not yet". */
export function injuryLines(i: InjuryAnswers | undefined): string[] {
  if (!i) return [];
  const out: string[] = [];
  if (i.hospital) out.push(`Doctor needed: ${i.hospital}`);
  if (i.food) out.push(`Blood near food: ${i.food}`);
  if (i.covered) out.push(`Covered with bandage and glove: ${i.covered}`);
  return out;
}

/** A reporter may raise a report's severity, never lower it. */
const SEVERITY_RANK: Record<Severity, number> = { low: 0, normal: 1, high: 2, urgent: 3 };
export function raiseSeverity(base: Severity, requested: Severity | undefined): Severity {
  return requested && SEVERITY_RANK[requested] > SEVERITY_RANK[base] ? requested : base;
}

/** True when the answers ask for more than first aid: a hospital-level injury raises the report to urgent. */
export const injuryIsSevere = (i: InjuryAnswers | undefined): boolean => i?.hospital === "yes";

/**
 * What the record says was ASKED. Never "done": the app cannot know it was thrown away or covered (J6).
 * An unanswered question asks nothing about itself, but the food line stays ("not answered" throws away, like "not sure").
 */
export function injuryAction(i: InjuryAnswers, state?: UsState): string {
  const asks: string[] = [];
  if (i.hospital === "yes") asks.push(`get medical care, call 911 for heavy bleeding, tell the person in charge, and ${packFor(state).workplace.askClause}`);
  if (i.food !== "no") asks.push("throw away food, drink or pastry that blood touched or might have touched, and clean and sanitize the tool and surface");
  if (i.covered !== "yes") asks.push("cover it with a waterproof bandage or finger cot and a single-use glove before food work, or do jobs away from food");
  return asks.length ? `Asked to: ${asks.join("; ")}. Not confirmed yet.` : "";
}

/**
 * The row for the Injury log. Who = initials, when = date and time (J5). Throws only for a row that cannot be honest: no who.
 * An unanswered question is written "not answered" (never a guess) and asks for the safe thing: throw away, cover (J6).
 * A row that needs no action (nothing near food, covered) carries no action text.
 */
export function injuryRowValues(o: { answers: InjuryAnswers; date: string; time: string; initials: string; state?: UsState }): { values: Record<string, string>; correctiveAction?: string } {
  if (!o.initials.trim()) throw new Error("Who was hurt is missing.");
  const a = o.answers;
  const action = injuryAction({ ...(a.hospital ? { hospital: a.hospital } : {}), food: a.food ?? "not sure", covered: a.covered ?? "not yet" }, o.state);
  const values = { date: o.date, time: o.time, hospital: a.hospital ?? "not answered", food: a.food ?? "not answered", covered: a.covered ?? "not answered", action, initials: o.initials.trim() };
  return { values, ...(action ? { correctiveAction: action } : {}) };
}

/** The entry body a client sends after the report (POST /entries). */
export function injuryEntryBody(o: { locationId: string; answers: InjuryAnswers; date: string; time: string; initials: string; state?: UsState }) {
  const row = injuryRowValues(o);
  return { locationId: o.locationId, templateId: INJURY_TEMPLATE, values: row.values, ...(row.correctiveAction ? { correctiveAction: row.correctiveAction } : {}) };
}

/** True when a report's answers are enough to write a record: any one question answered. */
export const injuryWorthRecording = (i: InjuryAnswers | undefined): boolean => !!i && (!!i.hospital || !!i.food || !!i.covered);

/**
 * The binder's lines for fc-bare-hand (prove.ts): how many injury records are on file, and how many of them answered the
 * food question. Neutral when there are none: a place with no injuries has nothing to show, and that is not a gap.
 */
export function injuryEvidence(entries: readonly { templateId: string; values?: Record<string, unknown> }[]): { tone: "go" | "warn" | "neutral"; text: string }[] {
  const mine = entries.filter((e) => e.templateId === INJURY_TEMPLATE);
  if (!mine.length) return [{ tone: "neutral", text: "Cuts and burns are recorded when they happen" }];
  const answered = mine.filter((e) => e.values?.food === "no" || e.values?.food === "yes" || e.values?.food === "not sure").length;
  const near = mine.filter((e) => e.values?.food === "yes" || e.values?.food === "not sure").length;
  const out: { tone: "go" | "warn" | "neutral"; text: string }[] = [
    { tone: "go", text: `${mine.length} cut or burn record${mine.length > 1 ? "s" : ""} with who and when (${INJURY_CITATION})` },
    { tone: answered === mine.length ? "go" : "neutral", text: `${answered} of ${mine.length} answered "blood near food?"` },
  ];
  if (near) out.push({ tone: "neutral", text: `${near} with food to throw away` });
  return out;
}
